import React, { useState, useEffect } from "react";
import { FacetNavigation } from "../Sidebar/Filters";
import { Product } from "~/common/Store/models/ProductListing";
import { Topper } from "~/common/Store/models/Topper";
import PaginationModel from "~/common/Store/models/Pagination";
import ProductListing from "../ProductListing";
import SpecializedPageHeader from "./SpecializedPageHeader";
import PageResult from "~/common/types/PageResult";
import FeatureFlagProvider from "~/common/contexts/FeatureFlagProvider";

interface Props extends PageResult {
  pageName: string;
  subtitle: string;
  altBackgroundImage: string;
  backgroundImage: string;
  description: string;
  dealExpiration: string;
  pageUrl: string;
  virtualUrl: string;
  isIndexed: boolean;
  couponCode: string;
  hideFilters: boolean;
  facetNavigation: FacetNavigation;
  products: Product[];
  pagination: PaginationModel;
  topper?: Topper;
  seoContent: string;
  hasCustomizableProductsOverride: boolean;
}

const SpecializedPage = ({
  featureFlags = {},
  pageName,
  subtitle,
  couponCode,
  dealExpiration,
  backgroundImage,
  altBackgroundImage,
  facetNavigation,
  products,
  hideFilters,
  pagination,
  pageUrl,
  topper,
  seoContent,
  hasCustomizableProductsOverride,
}: Props) => {
  const dealDate = new Date(dealExpiration);
  const [dealMessage, SetDealMessage] = useState("");
  const hasBackground = backgroundImage || altBackgroundImage;
  const hasHeaderData = subtitle || couponCode || hasBackground;

  /**
   * The server rendered date may be expired due to HTML caching.
   * Double check if coupon code is valid on the client.
   */
  useEffect(() => {
    // dealDate comes in as a pure date without a time component.
    // This will align the currentDate to that same format.
    const currentDate = new Date(new Date().toDateString());
    const message =
      dealDate >= currentDate
        ? `Deals end ${dealDate.toLocaleDateString()} at 11:59 PM EST`
        : "Deal Has Expired";
    SetDealMessage(message);
  }, [dealDate, hasHeaderData]);

  useEffect(() => {
    // hack to control classes for breadcrumb, remove this code when breadcrumb is rendered in react
    if (!hasHeaderData) {
      const breadcrumb = document.getElementsByClassName(
        "breadcrumb breadcrumb--inverse breadcrumb--specialized breadcrumb--absolute",
      );
      if (breadcrumb.length > 0) {
        breadcrumb[0]!.className = "breadcrumb";
      }
    }
    // end of removal
  });

  return (
    <FeatureFlagProvider featureFlags={featureFlags}>
      <div>
        {hasHeaderData && (
          <SpecializedPageHeader
            hasHeaderData={!!hasHeaderData}
            hasBackground={!!hasBackground}
            pageName={pageName}
            subtitle={subtitle}
            couponCode={couponCode}
            dealMessage={dealMessage}
            backgroundImage={backgroundImage}
          />
        )}
        <ProductListing
          aboveChromeComponent={
            <>
              {!hasHeaderData && (
                <div className="page-header-container flex items-center">
                  <div className="flex-1">
                    <h1 className="page-header">{pageName}</h1>
                    <h2 className="page-header2">{subtitle}</h2>
                  </div>
                </div>
              )}
            </>
          }
          facetNavigation={facetNavigation}
          hasBackground={!!hasBackground}
          hasCustomizableProductsOverride={hasCustomizableProductsOverride}
          hasHeaderData={!!hasHeaderData}
          hideFilters={hideFilters}
          pageName={hasHeaderData ? "" : pageName}
          pageUrl={pageUrl}
          pagination={pagination}
          prefetch
          products={products}
          topper={topper}
        />
        {seoContent && (
          <div className="container">
            <div
              className="padded-subtle small"
              dangerouslySetInnerHTML={{ __html: seoContent }}
            />
          </div>
        )}
      </div>
    </FeatureFlagProvider>
  );
};

export default SpecializedPage;
